import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, User, Phone, Plus, X, AlertTriangle } from 'lucide-react';

export default function CustomerSelector({
  customers = [],
  selectedCustomer,
  onSelect,
  onClear,
  onCreateNew,
  loading = false,
  placeholder = 'Select customer (optional)',
  variant = 'dropdown',
  enableKeyboard = false,
  onSkip,
  inputRef,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const innerSearchRef = useRef(null);
  const searchRef = inputRef || innerSearchRef;

  const customerName = (c) => c?.fullName || c?.name || '';
  const customerPhone = (c) => c?.phoneNumber || c?.phone || '';
  const customerKey = (c) => c?.customerId || c?.id || c?._id;

  const filtered = searchQuery.trim()
    ? customers.filter((c) => {
        const q = searchQuery.toLowerCase();
        return (
          customerName(c).toLowerCase().includes(q) ||
          customerPhone(c).toLowerCase().includes(q) ||
          (String(c?.cnicNumber || '').toLowerCase().includes(q))
        );
      })
    : customers;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if ((variant === 'full' || isOpen) && searchRef.current) {
      setTimeout(() => searchRef.current.focus(), 50);
    }
  }, [variant, isOpen]);

  const handleSelect = (customer) => {
    onSelect?.(customer);
    setIsOpen(false);
    setSearchQuery('');
    setActiveIndex(0);
  };

  // Keyboard-first mode: ArrowUp/Down walks the list, Enter selects the
  // highlighted customer, and Enter on an empty box skips the step.
  const handleKeyDown = (e) => {
    if (!enableKeyboard) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : Math.max(0, filtered.length - 1)));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // An empty box means "no customer"; a query means "pick the highlight".
      if (searchQuery.trim()) {
        const match = filtered[activeIndex];
        if (match) handleSelect(match);
        else onSkip?.();
      } else {
        onSkip?.();
      }
    } else if (e.key === 'Escape') {
      onSkip?.();
    }
  };

  useEffect(() => {
    if (enableKeyboard) setActiveIndex(0);
  }, [searchQuery, filtered.length, enableKeyboard]);

  const creditExceeded = selectedCustomer &&
    (selectedCustomer.currentCredit || 0) > (selectedCustomer.creditLimit || 0);

  const selName = customerName(selectedCustomer);
  const selPhone = customerPhone(selectedCustomer);
  const selKey = customerKey(selectedCustomer);

  const renderList = () => (
    <>
      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search by name, phone, or CNIC..."
            className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border-0 focus:outline-none focus:ring-1 focus:ring-primary-500 text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
        </div>
      </div>

      <div className={`overflow-y-auto scrollbar-thin ${variant === 'full' ? 'max-h-[45vh] min-h-[240px]' : 'max-h-72'}`}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">No customers found</p>
            <button
              onClick={() => {
                onCreateNew?.({ search: searchQuery });
                setIsOpen(false);
              }}
              className="mt-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              Create new customer
            </button>
          </div>
        ) : (
          filtered.map((customer, idx) => {
            const key = customerKey(customer) || idx;
            const isSelected = selectedCustomer && key && selKey && key === selKey;
            return (
              <button
                key={key}
                onMouseEnter={() => enableKeyboard && setActiveIndex(idx)}
                onClick={() => handleSelect(customer)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  enableKeyboard && idx === activeIndex
                    ? 'bg-primary-50 dark:bg-primary-900/20 ring-1 ring-inset ring-primary-500'
                    : isSelected
                    ? 'bg-primary-50 dark:bg-primary-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {customerName(customer)}
                    {isSelected && (
                      <span className="ml-2 text-[10px] text-primary-600 dark:text-primary-400">Selected</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                    <Phone size={10} />
                    {customerPhone(customer) || 'No phone'}
                  </p>
                </div>
                {customer.currentCredit > 0 && (
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400 dark:text-gray-500">Credit</p>
                    <p className="text-xs font-semibold text-amber-500">
                      Rs. {(customer.currentCredit || 0).toLocaleString()}
                    </p>
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 p-2">
        <button
          onClick={() => {
            onCreateNew?.({});
            setIsOpen(false);
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
        >
          <Plus size={14} />
          Add New Customer
        </button>
      </div>
    </>
  );

  const renderSelectedBanner = () =>
    selectedCustomer ? (
      <div className={`flex items-center gap-3 p-3 rounded-lg ${variant === 'full' ? 'bg-gray-50 dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'}`}>
        <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-sm">
          {(selName || '?').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{selName}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">{selPhone || 'No phone'}</p>
        </div>
        {selectedCustomer.currentCredit > 0 && (
          <div className="text-right flex-shrink-0">
            <p className="text-[10px] text-gray-400 dark:text-gray-500">Credit</p>
            <p className={`text-xs font-semibold ${creditExceeded ? 'text-red-500' : 'text-amber-500'}`}>
              Rs. {(selectedCustomer.currentCredit || 0).toLocaleString()}
            </p>
          </div>
        )}
        <button
          onClick={onClear}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    ) : null;

  if (variant === 'full') {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {selectedCustomer && (
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            {renderSelectedBanner()}
          </div>
        )}
        {creditExceeded && (
          <div className="mx-3 mt-3 flex items-center gap-1.5 text-xs text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
            <AlertTriangle size={12} />
            Credit limit exceeded! Current: Rs. {(selectedCustomer.currentCredit || 0).toLocaleString()} / Limit: Rs. {(selectedCustomer.creditLimit || 0).toLocaleString()}
          </div>
        )}
        {renderList()}
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      {renderSelectedBanner() || (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-400 dark:text-gray-500"
        >
          <User size={16} />
          <span>{placeholder}</span>
        </button>
      )}

      {creditExceeded && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
          <AlertTriangle size={12} />
          Credit limit exceeded! Current: Rs. {(selectedCustomer.currentCredit || 0).toLocaleString()} / Limit: Rs. {(selectedCustomer.creditLimit || 0).toLocaleString()}
        </div>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-1 z-50 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            {renderList()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}